// Static "Solutions" content, authored in-repo (SSG). Each solution renders
// at /solutions/[slug]. Copy is benefit-led and self-contained so the page is
// quotable by search engines and AI assistants alike. Content and imagery are
// synced from the live tablespace.com solution pages.

export type Solution = {
  slug: string;
  name: string;
  /** Sentence-case display headline for the detail page opener. */
  headline: string;
  /** Optional explicit line breaks for the mask-wipe headline. */
  headlineLines?: string[];
  /** One-line summary — index list + meta description. */
  summary: string;
  /** Legacy SEO overrides (from the old site) — used verbatim for <title>/description. */
  metaTitle?: string;
  metaDescription?: string;
  /**
   * Withdrawn from the site: hidden on the /solutions landing page, kept out
   * of the sitemap, and served with robots noindex. The page stays reachable
   * at its own URL so existing links and bookmarks still work — this only
   * stops it being listed or newly indexed.
   */
  retired?: boolean;
  /** Longer lede for the detail page opener. */
  intro: string;
  /** "What you get" — hairline-ruled benefits list. */
  highlights: { title: string; text: string }[];
  /** "How it works" — three-step strip. */
  steps: { title: string; text: string }[];
  hero: string;
  heroAlt: string;
  gallery: { src: string; alt: string }[];
  /** Optional sub-brand logo lockup (wordmark) — shown in the page header
   * and the homepage product card for the branded solutions. */
  logo?: string;
  /** Keep the logo's original colors in the page header (default renders it
   * white via an invert filter for the dark hero). */
  logoKeepColor?: boolean;
  /** Sub-brand wordmark shown above the "why choose" section heading in the
   * page body (light background variant). Used instead of a hero logo. */
  bodyLogo?: string;
  bodyLogoAlt?: string;

  // ── Optional live-content fields (backward-compatible) ──────────────────
  /** Heading for the "why choose" pillar grid (mirrors the live section). */
  whyChooseTitle?: string;
  /** Optional intro paragraph above the "why choose" pillar grid. */
  whyChooseIntro?: string;
  /** Optional inline link inside the why-choose intro (first match wraps). */
  whyChooseIntroLink?: { text: string; href: string };
  /** Pillar cards — e.g. Custom-Built: Lease/Design/Build/Operate. */
  whyChoose?: { title: string; text: string }[];
  /** Optional supporting photo for the "why choose" pillar grid. A 4-card
   * grid renders it as a sticky side column; a longer grid renders it as a
   * full-width lead-in banner above the cards. */
  whyChooseImage?: string;
  whyChooseImageAlt?: string;
  /** Optional supporting photo (banner) topping the "What you get" list. */
  highlightsImage?: string;
  highlightsImageAlt?: string;
  /** Flexible titled blocks each holding a grid of items (with optional
   * intro and per-item bullet lists) — Global Connect entry models / 7
   * benefits / 6 services; DESYN ecosystem / extended offerings; Enterprise
   * events / shoots / amplify / meeting rooms. Each block may carry an
   * optional supporting photo: an intro-only block (empty items) renders it
   * as an editorial 2-col split; a block with cards renders it as a
   * full-width lead-in banner under the heading. */
  /** Optional lead-in heading shown once above all the section blocks. */
  sectionsTitle?: string;
  sections?: {
    title: string;
    intro?: string;
    image?: string;
    imageAlt?: string;
    /** Optional sub-heading shown above the items (mirrors live sub-labels). */
    itemsTitle?: string;
    /** Render the items as a dropdown accordion (beside a sticky photo when
     * the block has one) instead of the card grid. */
    accordion?: boolean;
    /** Optional "Explore Now" CTA link shown under the block intro. */
    exploreLink?: { text: string; href: string };
    /** Optional "Book Now" CTA that reveals the app store links. */
    appLinks?: { ios: string; android: string };
    items: { title: string; text?: string; points?: string[] }[];
  }[];
  /** Amenities chips — Custom-Built & Suites. */
  amenitiesTitle?: string;
  amenities?: string[];
  /** Optional supporting photo shown beside the amenities chips. */
  amenitiesImage?: string;
  amenitiesImageAlt?: string;
  /** Footnote shown beneath the amenities chips. */
  amenitiesNote?: string;
  /** Amenities can render as icon cards (name + icon image) instead of chips. */
  amenityIcons?: { name: string; icon: string }[];
  /** Per-solution FAQ. */
  faqsTitle?: string;
  faqs?: { q: string; a: string }[];
  /** Optional supporting photo shown beside the FAQ accordion. */
  faqsImage?: string;
  faqsImageAlt?: string;
  /** Related articles/blog cards shown near the foot of the page. External
   * hrefs (press coverage) open in a new tab, matching the /media newsroom. */
  articlesTitle?: string;
  articles?: { title: string; href: string; image: string; category?: string }[];
  /** Optional press-quote strip shown after the FAQ (mirrors the live page). */
  pressQuote?: { text: string; source: string; href?: string };
  /** "Our centres" cards linking to centre pages. */
  centresTitle?: string;
  centres?: { name: string; href: string; image: string }[];
  /** Optional subtitle under the "We're Here to Reimagine Your Workspace"
   * enquiry band shown above the footer. */
  enquirySubtitle?: string;
  /** Show an extra free-text field in the enquiry form (placeholder text). */
  enquiryExtraField?: string;
};

export const solutions: Solution[] = [
  {
    slug: "custom-built",
    name: "Custom Built",
    metaTitle: "Custom-Built Office Space for Enterprises by Table Space",
    metaDescription:
      "Create custom-built office spaces with Table Space. Lease, design, build, and operate enterprise workspaces with consistency across India.",
    enquirySubtitle:
      "Plan a custom-built office space designed around your enterprise, your people, and your next stage of growth.",
    headline: "Custom-Built Offices",
    headlineLines: ["Custom-Built", "Offices"],
    summary:
      "Fully managed offices designed, built and operated to your brand and ways of working.",
    intro:
      "Custom Built by Table Space is an enterprise-focused workspace solution created for businesses that need consistency, control, and predictability while scaling across India.",
    // What-you-get and how-it-works are omitted for Custom-Built to match the
    // live page (they would otherwise duplicate the pillars and Explore block).
    highlights: [],
    steps: [],
    hero: "/images/pages/solutions/custom-built/goodearth-hero.webp",
    heroAlt: "Table Space Good Earth centre, Delhi NCR — custom-built workspace",
    gallery: [
      { src: "/images/pages/solutions/custom-built/custombuilt1.webp", alt: "Custom-built Table Space office designed to a client’s brand" },
      { src: "/images/pages/solutions/custom-built/custombuilt2.webp", alt: "Branded reception area in a custom-built Table Space office" },
      { src: "/images/pages/solutions/custom-built/custombuilt3.webp", alt: "Workspace interior delivered end-to-end through the Custom-Built model" },
      { src: "/images/pages/solutions/custom-built/custombuilt4.webp", alt: "Collaboration area in a custom-built enterprise office" },
      { src: "/images/pages/solutions/custom-built/custombuilt5.webp", alt: "Workstations in a custom-built Table Space office" },
      { src: "/images/pages/solutions/custom-built/custombuilt6.webp", alt: "Breakout space in a custom-built Table Space office" },
      { src: "/images/pages/solutions/custom-built/custombuilt7.webp", alt: "Detail of a custom-built Table Space office built to a client’s brand" },
      { src: "/images/pages/solutions/custom-built/custombuilt8.webp", alt: "Open collaboration zone in a custom-built enterprise office" },
      { src: "/images/pages/solutions/custom-built/custombuilt9.webp", alt: "Lifestyle amenity space within a custom-built Table Space office" },
      { src: "/images/pages/solutions/custom-built/custombuilt10.webp", alt: "Enterprise-ready custom-built workspace across India’s business cities" },
      { src: "/images/pages/solutions/custom-built/custombuilt11.webp", alt: "Meeting spaces tailored to a client’s ways of working" },
      { src: "/images/pages/solutions/custom-built/custombuilt12.webp", alt: "Workspace interior delivered end-to-end through the Custom-Built model" },
    ],
    whyChooseTitle: "Why choose Custom-Built offices?",
    whyChooseImage: "/images/pages/solutions/custom-built/custombuilt3.webp",
    whyChooseImageAlt:
      "Workspace interior delivered end-to-end through the Custom-Built model",
    highlightsImage: "/images/pages/solutions/custom-built/custombuilt7.webp",
    highlightsImageAlt:
      "Detail of a custom-built Table Space office built to a client’s brand",
    whyChooseIntro:
      "For global enterprises operating across multiple geographies, workspace delivery can become fragmented. Different vendors, varying standards, unclear timelines, and post-handover gaps often slow down expansion. Custom Built brings lease, design, build, and operations into one connected model, giving enterprises a unified delivery partner for custom-built office spaces.\n\nFrom design intent and cost transparency to delivery timelines and operational continuity, Table Space helps enterprises create workspaces that reflect their brand, support their people, and scale with long-term business growth.",
    whyChoose: [
      {
        title: "Lease",
        text: "Table Space provides access to premium Grade A properties across India’s leading business districts. Every location is evaluated for connectivity, infrastructure readiness, compliance, talent access, and long-term business suitability. This helps enterprises secure the right real estate without managing multiple disconnected property conversations across cities.",
      },
      {
        title: "Design",
        text: "Custom Built follows an enterprise-first design approach. We begin with people, business needs, workplace behaviour, brand intent, and growth priorities before moving into plans. The design process translates strategy into layouts, material direction, collaboration zones, experience areas, and operationally aligned workspace planning. Every detail is shaped to reflect the enterprise’s purpose and operating model.",
      },
      {
        title: "Build",
        text: "Custom Built operates on a program-led delivery framework supported by scale-led procurement. This brings greater clarity to construction timelines, material planning, execution quality, and budget visibility. Through coordinated procurement, timeline discipline, and quality control, Table Space helps preserve design intent through every stage of execution.",
      },
      {
        title: "Operate",
        text: "The workspace journey does not end at handover. Table Space bridges the gap between delivery and long-term operations by supporting daily service management, maintenance, upgrades, workplace experience, and performance alignment. This allows enterprises to focus on their business while Table Space manages the workspace experience across every touchpoint.",
      },
    ],
    sections: [
      {
        title: "Explore Custom-Built Offices by Table Space",
        intro:
          "Custom Built delivers more than a new office. It creates a consistent, enterprise-ready workspace that grows with your business. Whether you need a flagship headquarters, a modular enterprise office, or a pan-India network of custom-built workspaces, Table Space helps turn complex requirements into structured, high-performance environments across India’s top business cities.",
        image: "/images/pages/solutions/custom-built/custombuilt10.webp",
        imageAlt:
          "Enterprise-ready custom-built workspace across India’s business cities",
        items: [],
      },
    ],
    amenitiesTitle: "Amenities",
    amenitiesImage: "/images/pages/solutions/custom-built/custombuilt9.webp",
    amenitiesImageAlt:
      "Lifestyle amenity space within a custom-built Table Space office",
    amenities: [
      "Hospitality Team",
      "Work Café",
      "Phone Booth",
      "Meeting Room",
      "Valet Services",
      "Concierge Services",
      "Coffee Bar",
      "Parking Space",
      "Crèche",
      "Gym",
      "Recreation Zone",
      "Event Space",
      "Training Room",
      "Town Hall Space",
      "F&B Plaza",
      "Outdoor Patio",
      "Cafeteria",
    ],
    amenityIcons: [
      { name: "Hospitality Team", icon: "/images/pages/solutions/custom-built/amenities/Group-946.webp" },
      { name: "Work Café", icon: "/images/pages/solutions/custom-built/amenities/Group-948.webp" },
      { name: "Phone Booth", icon: "/images/pages/solutions/custom-built/amenities/Group-949.webp" },
      { name: "Meeting Room", icon: "/images/pages/solutions/custom-built/amenities/Group-950.webp" },
      { name: "Valet Services", icon: "/images/pages/solutions/custom-built/amenities/Group-947.webp" },
      { name: "Concierge Services", icon: "/images/pages/solutions/custom-built/amenities/Group-945.webp" },
      { name: "Coffee Bar", icon: "/images/pages/solutions/custom-built/amenities/Group-941-1.webp" },
      { name: "Parking Space", icon: "/images/pages/solutions/custom-built/amenities/Group-930.webp" },
      { name: "Crèche", icon: "/images/pages/solutions/custom-built/amenities/Group-939-1.webp" },
      { name: "Gym", icon: "/images/pages/solutions/custom-built/amenities/Group-933.webp" },
      { name: "Recreation Zone", icon: "/images/pages/solutions/custom-built/amenities/Group-932.webp" },
      { name: "Event Space", icon: "/images/pages/solutions/custom-built/amenities/Group-936-1.webp" },
      { name: "Training Room", icon: "/images/pages/solutions/custom-built/amenities/Group-937-1.webp" },
      { name: "Town Hall Space", icon: "/images/pages/solutions/custom-built/amenities/Group-935-1.webp" },
      { name: "F&B Plaza", icon: "/images/pages/solutions/custom-built/amenities/Group-934.webp" },
      { name: "Outdoor Patio", icon: "/images/pages/solutions/custom-built/amenities/Group-929.webp" },
      { name: "Cafeteria", icon: "/images/pages/solutions/custom-built/amenities/Group-942.webp" },
    ],
    amenitiesNote: "Amenities differ by each center*",
    faqsTitle: "Have more questions about Custom-Built offices?",
    faqs: [
      {
        q: "What does a custom-built workspace solution include?",
        a: "A custom-built workspace solution includes lease, design, build, and operations, all tailored to an enterprise’s business needs, brand standards, team size, operating model, and growth plans.",
      },
      {
        q: "How is Custom Built by Table Space different from traditional design-and-build models?",
        a: "Custom Built by Table Space is not just a design-and-build service. It brings lease, design, build, procurement, and operations into one connected workspace journey. Unlike traditional models that often end at handover, Custom Built supports enterprises with delivery consistency, post-handover support, and scalability across locations.",
      },
      {
        q: "What is the typical timeline for delivery?",
        a: "Delivery timelines depend on the size of the project, building readiness, and scope of work. Table Space can deliver enterprise-ready workspaces within defined timelines, supported by structured planning, coordinated procurement, and execution visibility.",
      },
      {
        q: "What locations are available for custom-built projects?",
        a: "Table Space supports custom-built office space requirements across India’s key business cities, including Bengaluru, Hyderabad, Noida, Gurugram, Delhi, Mumbai, Chennai, and Pune.",
      },
      {
        q: "What ongoing support is provided post move-in?",
        a: "Post move-in, Table Space supports daily operations, service delivery, maintenance, upgrades, account management, workplace experience, and performance alignment. This helps enterprises maintain a consistent workspace experience without managing multiple vendors.",
      },
    ],
    // No heading on the blog cards (per design) — empty string hides the h2.
    articlesTitle: "",
    articles: [
      {
        title: "India’s Geography & GCC Workspace Expansion",
        href: "/blog/indias-gcc-geography-expanding-across-key-cities",
        image:
          "/images/blog/indias-gcc-geography-expanding-across-key-cities.webp",
        category: "Insights",
      },
      {
        title:
          "How Managed Office Spaces Are Transforming India’s Commercial Office Space?",
        href: "/blog/how-managed-offices-transforming-india-office-market",
        image:
          "/images/blog/how-managed-offices-transforming-india-office-market.webp",
        category: "Managed Offices",
      },
      {
        title: "How Managed Offices Help Companies Expand Faster in India?",
        href: "/blog/managed-offices-help-companies-expand-faster-india",
        image:
          "/images/blog/managed-offices-help-companies-expand-faster-india.webp",
        category: "Managed Offices",
      },
    ],
    centresTitle: "Our Centres",
    centres: [
      {
        name: "Table Space Godrej Center Indiranagar",
        href: "/locations/bengaluru/godrej-indiranagar",
        image: "/images/centres/godrej-indiranagar.webp",
      },
      {
        name: "Table Space KRC Raheja Mindspace",
        href: "/locations/hyderabad/mindspace-building-4",
        image: "/images/centres/mindspace-building-4.webp",
      },
      {
        name: "Table Space R Square",
        href: "/locations/mumbai/r-square",
        image: "/images/centres/r-square.webp",
      },
      {
        name: "Table Space MFAR 2B Embassy Manyata Business Park",
        href: "/locations/bengaluru/mfar-2b-embassy-manyata-business-park",
        image: "/images/centres/mfar-2b-embassy-manyata-business-park.webp",
      },
      {
        name: "Table Space Towers Whitefield",
        href: "/locations/bengaluru/ts-towers-whitefield",
        image: "/images/centres/ts-towers-whitefield.webp",
      },
    ],
  },
  {
    slug: "suites",
    name: "Suites",
    metaTitle: "Ready-to-Move-in Office Space by Table Space",
    metaDescription:
      "Explore ready-to-move-in office spaces by Table Space, built for enterprises that need private, fully serviced suites across India’s top business cities.",
    enquirySubtitle:
      "Move into a ready-to-move-in office space designed for enterprise speed, privacy, and operational continuity.",
    headline: "Ready-To-Move-in Suites",
    headlineLines: ["Ready-To-Move-in", "Suites"],
    summary:
      "Fully fitted, plug-and-play office suites in prime business districts across 9 Indian cities — move-in ready.",
    intro:
      "Ready-to-move-in Suites by Table Space are premium private office spaces designed for enterprises that need speed, privacy, service, and consistency without waiting through traditional office fit-outs.",
    // What-you-get and how-it-works are omitted for Suites to match the live
    // page (the pillars and services sections carry that content).
    highlights: [],
    steps: [],
    hero: "/images/pages/about/atrium-hero.webp",
    heroAlt:
      "Table Space Atrium centre, Delhi NCR — ready-to-move-in suites",
    gallery: [
      { src: "/images/pages/solutions/suites/gallery-616.webp", alt: "Fully fitted private suite ready for immediate occupancy" },
      { src: "/images/pages/solutions/suites/gallery-617.webp", alt: "Workstations in a plug-and-play Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-618.webp", alt: "Breakout area within a serviced Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-619.webp", alt: "Meeting room inside a ready-to-move-in Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-620.webp", alt: "Reception area of a premium Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-621.webp", alt: "Collaboration zone in a serviced Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-622.webp", alt: "Private cabin within a Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-623.webp", alt: "Work café inside a Table Space suites centre" },
      { src: "/images/pages/solutions/suites/gallery-624.webp", alt: "Lounge seating within a ready-to-move-in suite" },
      { src: "/images/pages/solutions/suites/gallery-625.webp", alt: "Open workspace in a plug-and-play Table Space suite" },
      { src: "/images/pages/solutions/suites/gallery-626.webp", alt: "Enterprise-grade suite interior by Table Space" },
    ],
    whyChooseTitle: "Why Choose Ready-To-Move-in Suites",
    whyChooseImage: "/images/pages/solutions/suites/frame-276-8.webp",
    whyChooseImageAlt:
      "Fully fitted, ready-to-move-in Table Space suite in a prime business district",
    highlightsImage: "/images/pages/solutions/suites/frame-275-8.webp",
    highlightsImageAlt:
      "Plug-and-play private suite furnished and serviced from day one",
    whyChooseIntro:
      "Built for growing companies, MNCs, GCCs, and enterprise teams in India, Suites offer fully furnished, fully serviced office spaces that are ready for occupancy from day one. Each suite is planned to support focused work, collaboration, operational ease, and a professional brand presence.\n\nInstead of managing construction timelines, multiple vendors, IT setup, housekeeping, security, and daily operations separately, enterprises get a turnkey office space experience through one workspace partner. Ready-to-move-in Suites by Table Space help businesses move faster while maintaining the standards expected from enterprise-grade office environments.",
    whyChoose: [
      {
        title: "Premium Experience",
        text: "Ready-to-move-in Suites by Table Space are designed for enterprises that need private, high-quality office spaces without fit-out delays. Each suite is fully furnished, professionally managed, and planned to support comfort, functionality, collaboration, and brand presence. From workstations and meeting areas to support services and workplace experience, every element is created to help teams begin operations with minimal disruption.",
      },
      {
        title: "National Network",
        text: "Suites clients become part of the wider Table Space national network, with access to premium business locations across India’s leading commercial hubs. Enterprises can expand across Bengaluru, Hyderabad, Noida, Gurugram, Delhi, Mumbai, Chennai, and Pune while maintaining consistency in service, workspace quality, and operational support. This makes Suites suitable for companies planning a single private office, regional expansion, or a multi-city office presence.",
      },
      {
        title: "Secure & Compliant",
        text: "Ready-to-move-in Suites are built for enterprise-grade use, with attention to privacy, security, operational discipline, and workplace standards. Each suite supports a more controlled office environment compared to shared workspace models, giving enterprises the privacy they need for focused teams, client meetings, internal discussions, and daily business operations. Table Space also supports security, service management, maintenance, and workplace operations as part of the overall managed experience.",
      },
      {
        title: "Fast & Flexible",
        text: "Suites are created for enterprises that need speed without compromising on quality. Teams can move into ready-to-move-in office spaces within a shorter timeline, depending on availability, configuration, and business requirements. The model also supports flexibility as teams grow. Enterprises can start with a private suite, expand into larger spaces, or build a presence across multiple cities while continuing with the same workspace partner.",
      },
    ],
    sections: [
      {
        title: "Explore Ready-To-Move-in Suites by Table Space",
        intro:
          "Ready-to-move-in Suites by Table Space give enterprises immediate access to premium private office spaces without compromising on service, privacy, or operational support. Whether an organisation needs a single enterprise office suite, a flexible regional workspace, or a pan-India network of premium office spaces, Suites offer a fully serviced solution built for speed, consistency, and business continuity.",
        image: "/images/pages/solutions/suites/60.webp",
        imageAlt:
          "Premium private office suite in the Table Space national network",
        items: [],
      },
    ],
    amenitiesTitle: "Amenities",
    amenities: [
      "Hospitality Team",
      "Work Café",
      "Phone Booth",
      "Meeting Room",
      "Valet Services",
      "Concierge Services",
      "Coffee Bar",
      "Parking Space",
      "Crèche",
      "Gym",
      "Recreation Zone",
      "Event Space",
      "Town Hall Space",
      "Training Room",
      "F&B Plaza",
      "Outdoor Patio",
      "Cafeteria",
    ],
    amenityIcons: [
      { name: "Hospitality Team", icon: "/images/pages/solutions/suites/amenities/Group-946.webp" },
      { name: "Work Café", icon: "/images/pages/solutions/suites/amenities/Group-948.webp" },
      { name: "Phone Booth", icon: "/images/pages/solutions/suites/amenities/Group-949.webp" },
      { name: "Meeting Room", icon: "/images/pages/solutions/suites/amenities/Group-950.webp" },
      { name: "Valet Services", icon: "/images/pages/solutions/suites/amenities/Group-947.webp" },
      { name: "Concierge Services", icon: "/images/pages/solutions/suites/amenities/Group-945.webp" },
      { name: "Coffee Bar", icon: "/images/pages/solutions/suites/amenities/Group-941-1.webp" },
      { name: "Parking Space", icon: "/images/pages/solutions/suites/amenities/Group-930.webp" },
      { name: "Crèche", icon: "/images/pages/solutions/suites/amenities/Group-939-1.webp" },
      { name: "Gym", icon: "/images/pages/solutions/suites/amenities/Group-933.webp" },
      { name: "Recreation Zone", icon: "/images/pages/solutions/suites/amenities/Group-932.webp" },
      { name: "Event Space", icon: "/images/pages/solutions/suites/amenities/Group-936-1.webp" },
      { name: "Town Hall Space", icon: "/images/pages/solutions/suites/amenities/Group-935-1.webp" },
      { name: "Training Room", icon: "/images/pages/solutions/suites/amenities/Group-937-1.webp" },
      { name: "F&B Plaza", icon: "/images/pages/solutions/suites/amenities/Group-934.webp" },
      { name: "Outdoor Patio", icon: "/images/pages/solutions/suites/amenities/Group-929.webp" },
      { name: "Cafeteria", icon: "/images/pages/solutions/suites/amenities/Group-942.webp" },
    ],
    amenitiesNote: "Amenities differ by each center*",
    faqsTitle: "Have more questions about Ready-to-Move-in Suites?",
    faqs: [
      {
        q: "What is a ready-to-move-in office space?",
        a: "A ready-to-move-in office space is a fully furnished and operational private office that enterprises can occupy without going through a long fit-out process. Table Space offers ready-to-move-in Suites as fully serviced office spaces with workplace operations, support services, and essential infrastructure already in place.",
      },
      {
        q: "How quickly can we move in?",
        a: "Move-in timelines depend on availability, suite size, configuration, and business requirements. In most cases, ready-to-move-in Suites are designed to help enterprises start operations much faster than traditional leased office spaces or new fit-out projects.",
      },
      {
        q: "How do Suites differ from traditional leased offices or shared workspace models?",
        a: "Traditional leased offices often require separate planning, design, fit-out, vendor coordination, and operations management. Shared workspace models may not always offer the level of privacy or control enterprises need. Ready-to-move-in Suites by Table Space provide private, fully serviced office spaces with operational support from day one.",
      },
      {
        q: "Are Suites suitable for enterprise teams?",
        a: "Yes. Ready-to-move-in Suites are designed for enterprise teams that need privacy, scalability, premium amenities, and consistent service quality. They are suitable for growing teams, regional offices, GCC requirements, project teams, and companies looking for quick office deployment across India.",
      },
      {
        q: "What services are included in a fully serviced office space?",
        a: "Suites include essential workplace support such as internet readiness, IT support, housekeeping, security, utilities, maintenance, hospitality support, meeting spaces, and access to selected amenities. This helps enterprises operate from a professionally managed office without handling daily workspace management independently.",
      },
      {
        q: "Can we expand Suites across multiple cities?",
        a: "Yes. Table Space supports ready-to-move-in Suites across key Indian business cities, including Bengaluru, Hyderabad, Noida, Gurugram, Delhi, Mumbai, Chennai, and Pune. Enterprises can scale their private office presence across locations while maintaining consistent standards and service support.",
      },
      {
        q: "Can we manage and book workspace services digitally?",
        a: "Yes. The Table Space app supports workspace-related actions such as booking meeting rooms, raising service requests, and managing workplace needs from one digital interface.",
      },
    ],
    // No heading on the news cards (per design) — empty string hides the h2.
    articlesTitle: "",
    articles: [
      {
        title: "Table Space Launches Suites in Mumbai",
        href: "https://www.rprealtyplus.com/news-views/table-space-launches-suites-in-mumbai-118943.html",
        image: "/images/pages/solutions/suites/news-suites-mumbai.webp",
        category: "REALTY+",
      },
      {
        title:
          "IBM leases 1.1 lakh sq ft of office space from Table Space in Bengaluru",
        href: "https://www.hindustantimes.com/real-estate/ibm-leases-1-1-lakh-sq-ft-of-office-space-from-table-space-in-bengaluru-101743085769034.html",
        image: "/images/pages/solutions/suites/news-ibm-lease.webp",
        category: "Hindustan Times",
      },
      {
        title: "Table Space expands presence in Mumbai by 50%",
        href: "https://economictimes.indiatimes.com/industry/services/property-/-cstruction/table-space-expand-presence-in-mumbai-by-50/articleshow/118752080.cms",
        image: "/images/pages/solutions/suites/news-mumbai-50.webp",
        category: "The Economic Times",
      },
    ],
    centresTitle: "Our Centres",
    centres: [
      {
        name: "Table Space Godrej Center Indiranagar",
        href: "/locations/bengaluru/godrej-indiranagar",
        image: "/images/centres/godrej-indiranagar.webp",
      },
      {
        name: "Table Space KRC Raheja Mindspace",
        href: "/locations/hyderabad/mindspace-building-4",
        image: "/images/centres/mindspace-building-4.webp",
      },
      {
        name: "Table Space R Square",
        href: "/locations/mumbai/r-square",
        image: "/images/centres/r-square.webp",
      },
      {
        name: "Table Space MFAR 2B Embassy Manyata Business Park",
        href: "/locations/bengaluru/mfar-2b-embassy-manyata-business-park",
        image: "/images/centres/mfar-2b-embassy-manyata-business-park.webp",
      },
      {
        name: "Table Space Towers Whitefield",
        href: "/locations/bengaluru/ts-towers-whitefield",
        image: "/images/centres/ts-towers-whitefield.webp",
      },
    ],
  },
  {
    slug: "desyn",
    name: "DESYN",
    metaTitle: "Desyn - Table Space",
    // Wordmark now sits above the body "Core Proposition" heading (light
    // background variant) instead of over the dark hero.
    bodyLogo: "/images/pages/solutions/desyn/desyn-logo-design-in-sync.webp",
    bodyLogoAlt: "DESYN — Design in Sync",
    headline: "A New Standard for Enterprise Design & Build",
    headlineLines: ["A New Standard for Enterprise", "Design & Build"],
    summary:
      "Table Space’s in-house design and build practice — workplace strategy, interior design and turnkey delivery for enterprise offices.",
    intro:
      "DESYN by Table Space is our enterprise-focused Design & Build platform, created to move beyond the industry’s fragmented, project-led delivery model.\n\nWe built DESYN to solve a critical challenge faced by global enterprises operating in India: the lack of consistency and predictability while scaling across multiple geographies. Instead of managing multiple disconnected vendors across cities, DESYN offers a unified delivery partner, ensuring your design intent, cost transparency, and delivery timelines remain consistent, no matter where you expand.",
    // What-you-get and how-it-works are omitted — the core-proposition pillars
    // and "Why Choose DESYN" stages carry that content (matches the live page).
    highlights: [],
    steps: [],
    hero: "/images/pages/solutions/desyn/worldmark-hero.webp",
    heroAlt: "Table Space Worldmark centre, Delhi NCR — designed by DESYN",
    gallery: [
      {
        src: "/images/pages/solutions/desyn/interior-detail.webp",
        alt: "Interior detail from a DESYN-designed enterprise workspace",
      },
      {
        src: "/images/pages/solutions/desyn/collaboration-space.webp",
        alt: "Collaboration space delivered turnkey by DESYN",
      },
      {
        src: "/images/pages/solutions/desyn/carousel-mask.webp",
        alt: "Enterprise workspace delivered by DESYN, Table Space’s design and build practice",
      },
      {
        src: "/images/pages/solutions/desyn/carousel-frame-139.webp",
        alt: "Material and detail in a DESYN-designed enterprise workspace",
      },
      {
        src: "/images/pages/solutions/desyn/carousel-frame-142.webp",
        alt: "Workspace environment built turnkey by DESYN, Table Space’s design and build practice",
      },
    ],
    whyChooseTitle: "The Core Proposition\nof DESYN",
    whyChooseImage: "/images/pages/solutions/desyn/hero-workspace.webp",
    whyChooseImageAlt:
      "Enterprise workspace delivered by DESYN, Table Space’s design and build practice",
    whyChoose: [
      {
        title: "Predictable Execution",
        text: "DESYN operates on a program-led delivery framework backed by scale-led procurement. This removes uncertainty from construction timelines, enables accurate forecasting, and ensures predictable outcomes across cities and portfolios.",
      },
      {
        title: "Operational Continuity",
        text: "We bridge the gap between design and post-handover operations. From day one, your workspace is designed to function seamlessly, aligned to operational needs, experience standards, and long-term performance.",
      },
      {
        title: "Seamless Scalability",
        text: "Scale seamlessly across Custom-built offices and Ready-to-move-in Suites without re-evaluating vendors, systems, or standards, ensuring consistency as you expand into new locations.",
      },
      {
        title: "The Network Advantage",
        text: "Uniquely, all DESYN clients gain immediate access to the wider Table Space National Network. This includes on-demand use of boardrooms, training zones, experience centres, and coffee lounges across all our centres nationwide.",
      },
    ],
    sections: [
      {
        title: "Why Choose DESYN by ~Table Space?",
        intro:
          "Traditional Design & Build breaks at handover - every new phase resets, slowing enterprises down. DESYN by Table Space fixes the cycle by evolving where others stop.",
        image: "/images/pages/solutions/desyn/interior-detail.webp",
        imageAlt:
          "DESYN workspace where design intent is preserved through delivery",
        items: [
          {
            title: "Discover",
            text: "We begin with people, not plans - decoding behavior, brand, and business intent.",
          },
          {
            title: "Design",
            text: "Translate strategy into form, where every detail tells the brand’s story.",
          },
          {
            title: "Develop",
            text: "Align imagination with constructability - creativity that’s engineered.",
          },
          {
            title: "Deliver",
            text: "Precision meets transparency - design intent preserved in every finish.",
          },
          {
            title: "Evolve",
            text: "The workspace journey doesn’t end at handover; the Table Space experience partnership starts here.",
          },
        ],
      },
      {
        title: "The Table Space Ecosystem",
        image: "/images/pages/solutions/desyn/collaboration-space.webp",
        imageAlt:
          "Shared lounges and collaboration zones across the Table Space ecosystem",
        intro:
          "Your workspace should not reach a finish line, it should unlock a system built for growth. With DESYN by Table Space, you gain direct access to the Table Space ecosystem from day one: a network designed for expansion, continuity and enterprise-grade experiences across cities and clusters.\n\nFrom modular starts to multi-location scale-ups, your workspace evolves with your business, with the same partner and trust everywhere you grow.",
        itemsTitle: "Access to the Table Space Ecosystem",
        accordion: true,
        items: [
          {
            title: "Network | Scale | Expertise",
            text: "Over half a million sq. ft. of meeting rooms, lounges, boardrooms and training zones shape daily enterprise experiences across our network.",
          },
          {
            title: "Proprietary Tech-Stack",
            points: [
              "Complete Workspace life cycle tech stack built for enterprise",
              "Real time access with mobile interface",
              "White Labeled Enterprise App for seamless operations modules",
              "Complete Tech-Stack available for White-labelling for our Clients",
            ],
          },
          {
            title: "Employee Experience",
            points: [
              "Onboarding Excellence & Hypercare Post-Handover",
              "Dedicated Account Manager + Client Help Desk",
              "Quarterly Engagement & Continuous Feedback Loop",
              "Access to Table Space Ecosystem: Enterprise Services, F&B, concierge & curated experiences",
            ],
          },
        ],
      },
      {
        title: "Beyond the Workplace",
        image: "/images/pages/solutions/desyn/carousel-frame-139.webp",
        imageAlt:
          "Integrated workplace environment built beyond the core office by DESYN",
        intro:
          "Beyond our core offerings, DESYN extends into a broader layer of enterprise infrastructure, enabling organisations to design and deliver corporate accommodations, executive housing, and integrated workplace environments.\n\nFrom residential ecosystems for leadership teams to training campuses and support facilities, we bring the same precision, consistency, and operational thinking to every space your enterprise depends on, ensuring continuity in how you live, host, and grow.",
        itemsTitle: "Extended Offerings",
        items: [
          {
            title: "Corporate Accommodations",
            text: "Purpose-built living environments for employees and visiting teams, designed for comfort, efficiency, and proximity to work.",
          },
          {
            title: "Executive Housing",
            text: "High-quality residential ecosystems customised for leadership, balancing privacy, functionality, and brand-aligned design.",
          },
          {
            title: "Enterprise Training Campuses",
            text: "End-to-end design and build of large-scale learning and onboarding environments for enterprises, enabling continuous workforce development.",
          },
          {
            title: "Enterprise Support & Ancillary Facilities",
            text: "Integrated spaces including experience centres, operational hubs, and service infrastructure designed & built to strengthen the enterprise ecosystem.",
          },
        ],
      },
    ],
    faqsTitle: "Have more questions about\nDESYN by Table Space?",
    faqsImage: "/images/pages/solutions/desyn/interior-detail.webp",
    faqsImageAlt:
      "A DESYN-designed boardroom where enterprise teams meet and plan",
    faqs: [
      {
        q: "How is DESYN by Table Space different from traditional D&B models?",
        a: "DESYN by Table Space is not just a design and build service. It is a unified enterprise partner. Unlike traditional D&B models, DESYN by Table Space integrates design thinking, AI-powered delivery, and a curated partner ecosystem to turn complexity into consistent, scalable, and connected workspaces that grow with your enterprise.",
      },
      {
        q: "Can DESYN by Table Space be used for smaller modular projects?",
        a: "Yes. DESYN by Table Space’s flexible framework and modular approach make it suitable for projects of all sizes, allowing enterprises to implement solutions quickly without compromising on design integrity or operational efficiency.",
      },
      {
        q: "What kind of AI technology powers DESYN by Table Space?",
        a: "DESYN by Table Space uses an AI ecosystem that translates briefs, layouts, visuals, and budgets into a seamless workflow. Platforms like Delivery, SLA Management, Table Space Hawk, Client Platform, and AR/VR ensure that every step from concept to execution is data-driven, precise, and efficient.",
      },
      {
        q: "How does post-handover evolution work?",
        a: "The workspace journey does not end at handover. Through Table Space, enterprises get access to the full ecosystem, ongoing support, and operational tools. Programs like hypercare, dedicated account management, ESG initiatives, events, and employee experience programs ensure that every workspace continues to thrive and scale.",
      },
      {
        q: "How do we get started with a DESYN by Table Space Sprint?",
        a: "Getting started is simple. Engage with Table Space as your unified partner, and we will guide you through a DESYN by Table Space Sprint, combining design, technology, and operational planning to create a clear roadmap and actionable plan tailored to your enterprise needs.",
      },
    ],
    // No heading on the blog cards (per design) — empty string hides the h2.
    articlesTitle: "",
    articles: [
      {
        title:
          "Workspace Design Trends 2025: From Biophilic Elements to Modular Layouts",
        href: "/blog/workspace-design-trends-2025-from-biophilic-elements-to-modular-layouts",
        image:
          "/images/blog/workspace-design-trends-2025-from-biophilic-elements-to-modular-layouts.webp",
        category: "Design",
      },
      {
        title: "Top Trends in Commercial Office Interiors for 2025",
        href: "/blog/top-trends-in-commercial-office-interiors-for-2025",
        image:
          "/images/blog/top-trends-in-commercial-office-interiors-for-2025.webp",
        category: "Design",
      },
      {
        title:
          "Sustainability in Office Fit-Outs: Green Building Practices That Pay Off",
        href: "/blog/sustainability-in-office-fit-outs-green-building-practices-that-pay-off",
        image:
          "/images/blog/sustainability-in-office-fit-outs-green-building-practices-that-pay-off.webp",
        category: "Build",
      },
    ],
    centresTitle: "Our Centres",
    centres: [
      {
        name: "Table Space Godrej Center Indiranagar",
        href: "/locations/bengaluru/godrej-indiranagar",
        image: "/images/centres/godrej-indiranagar.webp",
      },
      {
        name: "Table Space KRC Raheja Mindspace",
        href: "/locations/hyderabad/mindspace-building-4",
        image: "/images/centres/mindspace-building-4.webp",
      },
      {
        name: "Table Space R Square",
        href: "/locations/mumbai/r-square",
        image: "/images/centres/r-square.webp",
      },
      {
        name: "Table Space MFAR 2B Embassy Manyata Business Park",
        href: "/locations/bengaluru/mfar-2b-embassy-manyata-business-park",
        image: "/images/centres/mfar-2b-embassy-manyata-business-park.webp",
      },
      {
        name: "Table Space Towers Whitefield",
        href: "/locations/bengaluru/ts-towers-whitefield",
        image: "/images/centres/ts-towers-whitefield.webp",
      },
    ],
  },
  {
    slug: "global-connect",
    name: "Global Connect",
    // Withdrawn from navigation and from search. Already unlinked from the
    // Solutions menu and footer; this also drops it from the sitemap and
    // serves it noindex. The URL still resolves for anyone holding a link.
    retired: true,
    metaTitle: "Global Connect - Table Space",
    // Wordmark now sits above the body "Why choose" heading (light background
    // variant) instead of over the dark hero.
    bodyLogo: "/images/pages/solutions/global-connect/global-connect-logo.webp",
    bodyLogoAlt: "Global Connect by Table Space",
    headline:
      "The Table Space Model for Building and Scaling Global Capability Centers (GCC’s) in India",
    headlineLines: [
      "The Table Space Model for Building and",
      "Scaling Global Capability Centers (GCC’s) in India",
    ],
    summary:
      "GCC enablement for international enterprises — set up and scale your India Global Capability Centre across 9 cities with one partner.",
    intro:
      "Global Connect by Table Space is a single integrated platform that enables global enterprises to establish, operate, and scale Global Capability Centers in India.",
    // What-you-get and how-it-works are omitted — the why-choose pillars and
    // entry-model/services blocks carry that content (matches the live page).
    highlights: [],
    steps: [],
    hero: "/images/pages/solutions/global-connect/senate-hero.webp",
    heroAlt: "Table Space Senate centre, Bengaluru — enterprise workspace for GCCs",
    gallery: [
      {
        src: "/images/pages/solutions/global-connect/gcc-workspace-1.webp",
        alt: "Enterprise GCC workspace enabled by Table Space Global Connect",
      },
      {
        src: "/images/pages/solutions/global-connect/gcc-workspace-2.webp",
        alt: "Collaboration floor in a Table Space Global Capability Centre",
      },
      {
        src: "/images/pages/solutions/global-connect/gallery-1.webp",
        alt: "Pan-India workspace supporting a scaling GCC operation",
      },
      {
        src: "/images/pages/solutions/global-connect/gallery-2.webp",
        alt: "Managed GCC workspace within the Table Space national network",
      },
      {
        src: "/images/pages/solutions/global-connect/gallery-3.webp",
        alt: "Enterprise-grade collaboration space for an India GCC",
      },
    ],
    whyChooseTitle: "Why choose Global Connect by Table Space?",
    whyChooseImage: "/images/pages/solutions/global-connect/gcc-workspace-1.webp",
    whyChooseImageAlt:
      "Global Capability Centre workspace operated end-to-end by Table Space",
    whyChooseIntro:
      "It consolidates all critical GCC lifecycle services - entity incorporation and regulatory compliance, turnkey workspaces, talent acquisition and people operations, IT/tech infrastructure, marketing localization, sustainability/ESG frameworks, and ongoing governance – as one unified partner.\n\nWhile enterprises can establish GCCs in India faster than ever, fragmented providers create friction across the full lifecycle. Global Connect removes this complexity by delivering end-to-end enablement through one operating framework, ensuring speed, predictability, and single-point accountability.",
    whyChooseIntroLink: { text: "GCCs in India", href: "/solutions/global-capability-center" },
    whyChoose: [
      {
        title: "Single-Point Ownership",
        text: "One operating partner across workspace, talent, technology, compliance, and experience.",
      },
      {
        title: "Distributed On-Ground Teams",
        text: "City-level operations, HR, IT, and compliance teams for real-time accountability and faster resolution.",
      },
      {
        title: "Seamless Multi-City Scale",
        text: "Scale within and across cities without disruption, re-negotiation, or complexity.",
      },
      {
        title: "Enterprise Governance Built-In",
        text: "One operating partner across workspace, talent, technology, compliance, and experience.",
      },
      {
        title: "End-to-End Experience Layer",
        text: "Concierge-led management of travel, mobility, logistics, and workplace services.",
      },
      {
        title: "Pan-India Ecosystem Access",
        text: "Access to meeting, training, collaboration, and amenity spaces across India.",
      },
      {
        title: "Growth-Aligned Workspace Journey",
        text: "Move from pilot teams to large, custom-built campuses seamlessly as you scale.",
      },
    ],
    sections: [
      {
        title: "Unlock Flexible Entry Models",
        intro:
          "Our multi model approach ensures companies can test, scale, and eventually own their GCC with minimal risk and complete clarity.",
        image: "/images/pages/solutions/global-connect/gallery-1.webp",
        imageAlt:
          "Flexible GCC entry models supported within the Table Space network",
        items: [
          {
            title: "On-Demand GCC",
            points: [
              "Modular - pick and choose from a suite of services",
              "Covers the spectrum from entity set up to day-to-day operations",
            ],
          },
          {
            title: "BOT (Build-Operate-Transfer)",
            points: [
              "Structured, low-risk pathway for large-scale India setup",
              "Table Space builds and stabilizes the entire GCC ecosystem - people, process, and infrastructure - before a seamless transfer of ownership to the client",
            ],
          },
          {
            title: "Captive-as-a-Service",
            points: [
              "Fastest way to launch in India",
              "A turnkey operating model where the GCC is built - utilizing fully managed, secure “pods” within the Table Space national network on behalf of the client for immediate day-one productivity",
            ],
          },
        ],
      },
      {
        title: "Explore Our Host of Services",
        image: "/images/pages/solutions/global-connect/gallery-2.webp",
        imageAlt:
          "Integrated GCC services delivered through one Global Connect partner",
        accordion: true,
        items: [
          {
            title:
              "Legal, Compliance, Financial & Entity Setup-as-a-Service (BOT Ready)",
            text: "The Global Connect Finance and Legal team deliver structured guidance and advisory support across the full entity creation journey in India. From entity setup, registrations, and statutory compliance to ongoing compliance, contracting, vendor management, and risk mitigation, we ensure faster market entry with minimal regulatory risk.",
          },
          {
            title: "Talent Acquisition & Leadership Hiring-as-a-Service",
            text: "Global Connect by Table Space delivers end-to-end talent acquisition designed for scale. With integrated ATS, sourcing, and assessment platforms, dedicated recruiting pods, and real-time hiring dashboards, we enable faster time-to-hire with consistent talent quality. From entry-level to senior and executive leadership roles, including GCC heads and functional leaders, our approach combines workforce planning, hiring velocity optimization, and employer branding to help enterprises build strong, scalable teams in India.",
          },
          {
            title: "HR, Payroll & People Operations-as-a-Service",
            text: "Global Connect by Table Space delivers embedded people operations to build brand, talent, and market presence at scale. With a unified HRIS and people analytics platform, standardized policies, and dedicated HR operations and compliance teams, we manage the full HR lifecycle from onboarding to exits.",
          },
          {
            title: "Marketing-as-a-Service",
            text: "Global Connect enables strategic employer branding and market positioning to strengthen brand visibility and talent attraction in India. Through deep industry and market research, we define a compelling Employer Value Proposition and brand identity, identify competitive gaps, and position the brand effectively with candidates, customers, and stakeholders.",
          },
          {
            title: "IT, Security & Tech Operations-as-a-Service",
            text: "Our end-to-end delivery covers end-user computing, device and asset lifecycle management, IT service desk and change management, identity and access controls, cybersecurity, and core network and collaboration tools.",
          },
          {
            title: "HSE & Sustainability-as-a-Service",
            text: "Global Connect provides HSE and sustainability as a service, enabling ESG-ready GCC operations from Day 1. Through energy monitoring and optimization, waste management and green initiatives, and ESG data capture and reporting support, we deliver measurable sustainability impact.",
          },
        ],
      },
    ],
    faqsTitle: "Have more questions about Global Connect by Table Space?",
    faqs: [
      {
        q: "How do I set up a Global Capability Center (GCC) in India quickly?",
        a: "Setting up a GCC in India traditionally takes 6 to 9 months, but Global Connect by Table Space compresses this timeline through ready to use infrastructure and pre-established compliance frameworks. By leveraging our Captive-as-a-Service model, enterprises can achieve Day 1 productivity with secure, managed pods, bypassing the typical delays of real estate procurement and entity incorporation.",
      },
      {
        q: "What is the difference between BOT and Captive as a Service model?",
        a: "The Build Operate Transfer (BOT) model is a structured, low risk pathway where Table Space builds the entire ecosystem including talent, process, and workspace before transferring ownership to you. In contrast, Captive-as-a-Service is a turnkey plug and play solution designed for immediate market entry, utilizing Table Space’s existing national network to host your operations without the upfront overhead of a custom campus.",
      },
      {
        q: "How does Global Connect handle regulatory and legal compliance for new GCCs?",
        a: "Our integrated Entity Setup as a Service manages the full regulatory lifecycle in India. This includes entity incorporation, GST registrations, labor law compliance, and ongoing governance. We act as your local expert, mitigating the risks of navigating India’s complex legal landscape and ensuring your GCC remains fully compliant from day one.",
      },
      {
        q: "Can I scale my GCC operations across multiple cities in India?",
        a: "Yes. Global Connect is built for a seamless multi-city scale. Whether you are piloting a small team in Bengaluru or expanding to a large-scale campus in Pune or Gurgaon, our pan India ecosystem allows you to scale without the friction of renegotiating multiple vendor contracts or managing fragmented service providers.",
      },
    ],
    // No heading on the blog cards (per design) — empty string hides the h2.
    articlesTitle: "",
    articles: [
      {
        title: "How Are Global Companies Setting Up GCCs in India?",
        href: "/blog/gcc-setup-in-india",
        image: "/images/blog/gcc-setup-in-india.webp",
        category: "GCC",
      },
      {
        title:
          "GCC Operating Models: How Enterprises Can Balance Control, Speed, and Scalability",
        href: "/blog/gcc-operating-models-india",
        image: "/images/blog/gcc-operating-models-india.webp",
        category: "GCC",
      },
      {
        title:
          "How Did a Global Company Launch a Full GCC Operation in India in Under 90 Days?",
        href: "/blog/setting-up-a-captive-center-in-india",
        image: "/images/blog/setting-up-a-captive-center-in-india.webp",
        category: "Case Study",
      },
    ],
    centresTitle: "Our Centres",
    centres: [
      {
        name: "Table Space Godrej Center Indiranagar",
        href: "/locations/bengaluru/godrej-indiranagar",
        image: "/images/centres/godrej-indiranagar.webp",
      },
      {
        name: "Table Space KRC Raheja Mindspace",
        href: "/locations/hyderabad/mindspace-building-4",
        image: "/images/centres/mindspace-building-4.webp",
      },
      {
        name: "Table Space R Square",
        href: "/locations/mumbai/r-square",
        image: "/images/centres/r-square.webp",
      },
      {
        name: "Table Space MFAR 2B Embassy Manyata Business Park",
        href: "/locations/bengaluru/mfar-2b-embassy-manyata-business-park",
        image: "/images/centres/mfar-2b-embassy-manyata-business-park.webp",
      },
      {
        name: "Table Space Towers Whitefield",
        href: "/locations/bengaluru/ts-towers-whitefield",
        image: "/images/centres/ts-towers-whitefield.webp",
      },
    ],
  },
  {
    slug: "enterprise-services",
    name: "Enterprise Services",
    enquirySubtitle:
      "Reach out to learn more about our range of products and services, we’re here to support you with the answers you need.",
    headline: "Enterprise Office & Workspace Solutions",
    headlineLines: ["Enterprise Office &", "Workspace Solutions"],
    summary:
      "End-to-end services that go beyond the desk — events, shoots, advertising and meeting spaces, all under one roof.",
    intro:
      "Elevate your business with end-to-end solutions designed to go beyond your desk. From advertising and events to space management and essential business services, we provide everything you need under one roof, so you can focus on what matters most: growing your business.",
    // The four services below are detailed as full section blocks, so the
    // generic "What you get" list is intentionally left empty here.
    highlights: [],
    steps: [],
    hero: "/images/pages/solutions/enterprise-services/senate-hero.webp",
    heroAlt: "Table Space Senate centre, Bengaluru — enterprise workspace and event space",
    gallery: [
      {
        src: "/images/pages/solutions/enterprise-services/Frame-839-3.webp",
        alt: "Hospitality-grade reception within a Table Space enterprise workspace",
      },
      {
        src: "/images/pages/solutions/enterprise-services/Frame-745-3.webp",
        alt: "Flexible event space in a Table Space enterprise office",
      },
      {
        src: "/images/pages/solutions/enterprise-services/Frame-744-3.webp",
        alt: "Meeting room equipped with enterprise-grade AV at Table Space",
      },
    ],
    sectionsTitle: "Explore solutions within a\nseamless workspace ecosystem",
    sections: [
      {
        title: "Events",
        intro:
          "Bring your events to life in premium, fully managed spaces designed for impact and precision.",
        image: "/images/pages/solutions/enterprise-services/Frame-744-3.webp",
        imageAlt:
          "Flexible event space in a Table Space enterprise office set up for a townhall",
        accordion: true,
        items: [
          {
            title: "Versatile Venues",
            points: [
              "Host large-scale events, townhalls, pop-ups, and roadshows",
              "Beautifully designed, flexible spaces tailored to diverse formats and requirements",
            ],
          },
          {
            title: "Spacious & Scalable",
            points: [
              "Accommodate up to 200 guests in expansive, fully equipped venues",
              "Designed to blend creativity with enterprise-grade professionalism",
            ],
          },
          {
            title: "End-to-End Support",
            points: [
              "Dedicated on-ground teams ensure seamless coordination and execution",
              "Access trusted partners for catering, audiovisuals, décor, and logistics",
              "Every detail is managed to deliver a flawless experience that leaves a lasting impression",
            ],
          },
        ],
      },
      {
        title: "Shoots",
        intro:
          "Whether it’s a photoshoot or your next film sequence, our spaces are ready to bring your vision to life.",
        image: "/images/pages/solutions/enterprise-services/Frame-839-3.webp",
        imageAlt:
          "Aesthetic Table Space interior used as a backdrop for a film or photoshoot",
        accordion: true,
        items: [
          {
            title: "Well-designed spaces",
            points: [
              "Use our aesthetic spaces as a backdrop for your next film, video, or photoshoot",
              "Get access to our amenities and support from a dedicated team",
            ],
          },
        ],
      },
      {
        title: "Amplify",
        intro:
          "Showcase your brand where it matters most with exclusive placements in high-impact office spaces.",
        image: "/images/pages/solutions/enterprise-services/Frame-745-3.webp",
        imageAlt:
          "High-traffic Table Space environment carrying brand placements and signage",
        accordion: true,
        items: [
          {
            title: "Strategic Visibility",
            points: [
              "Exclusive branding opportunities across digital screens, standees, and tent cards",
              "Positioned in high-traffic zones to maximize exposure and engagement",
            ],
          },
          {
            title: "Targeted Impact",
            points: [
              "Designed for enterprises aiming to strengthen visibility and brand presence",
              "Reach decision-makers and professionals within India’s most premium office environments",
            ],
          },
          {
            title: "Seamless Integration",
            points: [
              "Branding placements that blend naturally with the workspace aesthetic",
              "Deliver a subtle yet powerful brand experience across everyday touchpoints",
            ],
          },
        ],
      },
      {
        title: "Meeting room | Training room\n| ~Board room",
        intro:
          "Table Space offers premium Meeting Rooms, Board Rooms, and Training Rooms designed for teams who value productivity and clarity. Located in India’s top-tier business districts, each space is impeccably maintained and fully equipped with enterprise-grade technology. Whether you’re hosting a client, aligning with your team, or setting up a hybrid collaboration, you can book the room you need with the right amenities directly from the new Table Space App.",
        image: "/images/pages/solutions/enterprise-services/Frame-840-3-1.webp",
        imageAlt:
          "Meeting room equipped with enterprise-grade AV in a Table Space centre",
        accordion: true,
        exploreLink: { text: "Explore Now", href: "/enterprise/meeting-rooms" },
        appLinks: {
          ios: "https://apps.apple.com/in/app/table-space/id6749477082",
          android:
            "https://play.google.com/store/apps/details?id=com.tablespacev2.app",
        },
        items: [
          {
            title: "Extensive Network",
            points: [
              "Over 170 meeting rooms across 70+ centers in India’s leading business districts",
              "Strategically located within prime Grade-A commercial properties",
            ],
          },
          {
            title: "Smart Design",
            points: [
              "Thoughtfully planned spaces that balance functionality and aesthetics",
              "Acoustic insulation ensures focus and clarity in every discussion",
            ],
          },
          {
            title: "Seamless Connectivity",
            points: [
              "High-speed internet and enterprise-grade AV systems for uninterrupted meetings",
              "Equipped with video conferencing tools and projectors to support hybrid collaboration",
            ],
          },
          {
            title: "Enhanced Comfort",
            points: [
              "Ergonomic seating and whiteboards designed to drive productivity and engagement",
            ],
          },
        ],
      },
    ],
    // No heading on the blog cards (per design) — empty string hides the h2.
    articlesTitle: "",
    articles: [
      {
        title:
          "The Future of Workspace Management: Integrating Tech & Hospitality",
        href: "/blog/the-future-of-workspace-management-integrating-tech-hospitality",
        image:
          "/images/blog/the-future-of-workspace-management-integrating-tech-hospitality.webp",
        category: "Workspace Experience",
      },
      {
        title: "10 Practical Ways to Boost Workplace Efficiency",
        href: "/blog/10-proven-strategies-to-improve-workplace-efficiency",
        image:
          "/images/blog/10-proven-strategies-to-improve-workplace-efficiency.webp",
        category: "Workplace",
      },
      {
        title: "How IoT-Enabled Infrastructure Creates Smarter Workplaces",
        href: "/blog/how-iot-enabled-infrastructure-creates-smarter-workplaces",
        image:
          "/images/blog/how-iot-enabled-infrastructure-creates-smarter-workplaces.webp",
        category: "Technology",
      },
    ],
    centresTitle: "Our Centres",
    centres: [
      {
        name: "Table Space Godrej Center Indiranagar",
        href: "/locations/bengaluru/godrej-indiranagar",
        image: "/images/centres/godrej-indiranagar.webp",
      },
      {
        name: "Table Space KRC Raheja Mindspace",
        href: "/locations/hyderabad/mindspace-building-4",
        image: "/images/centres/mindspace-building-4.webp",
      },
      {
        name: "Table Space R Square",
        href: "/locations/mumbai/r-square",
        image: "/images/centres/r-square.webp",
      },
      {
        name: "Table Space MFAR 2B Embassy Manyata Business Park",
        href: "/locations/bengaluru/mfar-2b-embassy-manyata-business-park",
        image: "/images/centres/mfar-2b-embassy-manyata-business-park.webp",
      },
      {
        name: "Table Space Towers Whitefield",
        href: "/locations/bengaluru/ts-towers-whitefield",
        image: "/images/centres/ts-towers-whitefield.webp",
      },
    ],
  },
];

export const solutionBySlug = (slug: string) =>
  solutions.find((s) => s.slug === slug);
